import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import ComplianceViolationsPresentational from './compliance-violations-presentational';
import ComplianceSuccess from './compliance-success';
import { ComplianceService } from 'src/services';
import ContentLoader from 'src/components/shared/content-loader';
import moment from 'moment';
import { message } from 'antd';
import EmployeeService from 'src/services/employee-service';
import { useAppInsightsContext, useTrackEvent } from "@microsoft/applicationinsights-react-js";
import { useSelector } from 'react-redux';
import { isFileTypeAccepted } from '@pages/DesignSystem/Dragger/fileTypeUtils';

const justificationDocumentsMap = {
	3: 'Payslips for all backdated salaries processed',
	4: 'Annual leaves calculation',
	5: 'Payslip indicating Air ticket amount',
	6: 'Expense receipts',
	7: 'Payslips indicating calculation of additional compensation',
	8: 'Loan request and approval document',
	9: 'End of service calculation',
	10: 'Documented evidence clarifying the additional allowance/ compensation amount'
};

const COMPLIANCE_SUBMISSION_TYPES = ['Compliance_RFIRequest', 'Compliance_Approve', 'Compliance_Reject'];

const ComplianceViolationsFunctional = () => {
	const appInsights = useAppInsightsContext();
	const trackCLLSubmission = useTrackEvent(appInsights, "Card Load Limit Submission", {});
	const trackLoadDataError = useTrackEvent(appInsights, "Compliance Load Data Error", {});
	const data = useSelector((state) => state?.sharedStates);
	const currentUser = data?.currentUser;
	const { complianceCaseId, employeeId, } = useParams();
	const history = useHistory();
	const complianceService = new ComplianceService();
	const employeeService = new EmployeeService();
	const historyState = history.location?.state;
	// If the user visits the page not through the manage employees page, redirect to it
	useEffect(() => {
		if (!historyState) {
			history.replace('/employees/manage_employee');
		}
	}, []);
	const isDormantEmployee = historyState?.recordStatus === 'D';
	const resolutionType = isDormantEmployee ? 'ReactivateCardRequest' : 'NotToDeleteCardRequest';

	const [complianceData, setComplianceData] = useState({
		employeeName: historyState?.employeeName,
		employeeId: employeeId,
		comments: '',
		justification: '',
		contractType: '',
		contractExpiryDate: '',
		justifications: ''
	});

	const [laborContractUploadStatus, setLaborContractUploadStatus] = useState('new');
	const [laborContractFile, setLaborContractFile] = useState(null);
	const [justificationFiles, setJustificationFiles] = useState([]);
	const [justificationOptions, setJustificationOptions] = useState([]);
	const [selectedJustificationOptions, setSelectedJustificationOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
	const [previousJustificationDocuments, setPreviousJustificationDocuments] = useState([]);
	const [isRFI, setIsRFI] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [showCommentSection, setShowCommentSection] = useState(false);
	const [dateError, setDateError] = useState('');
	const [laborContractFileError, setLaborContractFileError] = useState('');
	const [rejectedLaborContractFileName, setRejectedLaborContractFileName] = useState(null);
	const [justificationFilesError, setJustificationFilesError] = useState('');

	// Validations
	const isDateValid = complianceData.contractType === 'Unlimited' ||
		(complianceData.contractExpiryDate && !dateError);
	const requiresJustificationDocument = selectedJustificationOptions.some(
		option => option.requiresDocument === true
	);
	const hasComments = !showCommentSection || (complianceData.justification && complianceData.justification.trim().length > 0);


	// Collect all required documents from selected justification options
	const requiredDocumentsList = (() => {
		if (!requiresJustificationDocument) return [];

		const commonDocuments = [
			"Valid Employment Contract",
			"Copy of EID",
			"Payslips for the last 6 months"
		];

		// Add specific documents based on selected options
		const specificDocuments = selectedJustificationOptions
			.filter(option => option.requiresDocument === true)
			.map(option => justificationDocumentsMap[option.id])
			.filter(doc => doc !== undefined);

		return [...commonDocuments, ...specificDocuments];
	})();

	// Check if justification documents are provided (either newly uploaded or previously uploaded)
	// Only count files without errors
	const validJustificationFiles = justificationFiles.filter(file => !file.error);
	const hasJustificationDocuments = validJustificationFiles.length > 0 || previousJustificationDocuments.length > 0;


	// Form is valid if:
	// 1. Labor contract requirements are met (file, type, valid date) and no labor contract error
	// 2. If any selected justification requires documents, at least one document must be uploaded
	// 3. No justification file has an error (size or format)
	// 4. If comments section is visible, comments must be provided
	// 5. If it's an initial submission (!isRFI), justification options are required
	const hasJustificationOptions = selectedJustificationOptions.length > 0;
	const hasNoJustificationFileErrors = !justificationFiles.some((file) => file.error);
	const isFormValid = laborContractFile && complianceData.contractType && isDateValid &&
		!laborContractFileError &&
		(!requiresJustificationDocument || hasJustificationDocuments) &&
		hasNoJustificationFileErrors &&
		hasComments &&
		(isRFI || hasJustificationOptions);



	const loadJustificationOptions = async () => {
		// Load justification options and transform them for MultiSelect
		const optionsResponse = await complianceService.getJustificationOptions();
		const transformedOptions = (optionsResponse || []).map((option) => ({
			...option,
			name: option.reason,
			key: option.id,
		}));
		setJustificationOptions(transformedOptions);
	};

	const loadData = async () => {
		try {
			const caseResponse = await complianceService.getComplianceCase(complianceCaseId);
			const caseData = caseResponse.data;
			// If a resolution hasn't been created yet, there would be no data to load
			// If the employee had a previous case and was then moved to dormancy, we treat this as a new case.
			// Employee in Need Attention Tab -> Submits Justification -> Justification Rejected -> Employee moved to Deleted Tab -> Request for reactivation can be made for this employee, as a new case (not RFI).
			if (!caseData || (isDormantEmployee && historyState?.hasReactivation)) {
				setIsRFI(false);
				return;
			}

			// Aggregate documents from all submissions
			const documents = (caseData.submissions || []).reduce((acc, submission) => [...acc, ...submission.documents], []).map(doc => ({
				name: doc.fileName,
				id: doc.documentId,
				documentType: doc.documentType,
				contractType: doc.contractType,
				expiryDate: doc.expiryDate,
			}));

			const previousContractFile = documents.find(doc => doc.documentType === 'LaborContract');
			setLaborContractFile(previousContractFile);
			setPreviousJustificationDocuments(documents.filter(doc => doc.documentType !== 'LaborContract'));

			// Filter submissions with comments, sort by submittedAt, then extract comments
			const comments = (caseData.submissions || []).filter(submission =>
				COMPLIANCE_SUBMISSION_TYPES.includes(submission.type) && submission.comment
			).sort((a, b) => moment(b.submittedAt || 0) - moment(a.submittedAt || 0))
				.map(submission => submission.comment);

			setShowCommentSection(!!comments.length);

			setComplianceData((prev) => ({
				...prev,
				comments,
				contractType: previousContractFile?.contractType || '',
				contractExpiryDate: previousContractFile?.expiryDate || '',
			}));
		} catch (err) {
			console.error('Error loading compliance data:', err);
			trackLoadDataError({
				'Event Type': 'Log',
				'Action Taken': 'Compliance Case Data Loading',
				'User Identification': currentUser?.user?.userName,
				'Event Source': 'Payroll Portal Application',
				'Details': 'Compliance Case Data Loading Error',
				'Error': err?.message || String(err),
				'Compliance Case ID': complianceCaseId,
				'Employee ID': employeeId,
			});
			appInsights.core.flush();
			setLoadError(true);
			setIsRFI(false);
		}
	};

	const loadAllData = async () => {
		setLoadError(false);
		setLoading(true);
		try {
			if (complianceCaseId) {
				await loadData();
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadAllData();
		loadJustificationOptions();
	}, [complianceCaseId]);

	const handleJustificationChange = (value) => {
		setComplianceData((prev) => ({
			...prev,
			justification: value,
		}));
	};

	const handleContractTypeChange = (value) => {
		setComplianceData((prev) => ({
			...prev,
			contractType: value,
		}));

		if (value === 'Unlimited') {
			setDateError('');
		} else if (value === 'Limited' && !complianceData.contractExpiryDate) {
			setDateError('Contract expiry date is required for Limited contracts');
		}
	};

	const handleDateChange = (date) => {
		if (!date || !date[0]) {
			setComplianceData((prev) => ({
				...prev,
				contractExpiryDate: '',
			}));
			// Only set error if it's a Limited contract
			if (complianceData.contractType === 'Limited') {
				setDateError('Contract expiry date is required for Limited contracts');
			} else {
				setDateError('');
			}
			return;
		}

		const parsedDate = moment(date[0]).format('DD/MM/YYYY');
		const selectedDate = moment(date[0]);
		const today = moment().startOf('day');

		// Validate that the date is in the future
		if (!selectedDate.isAfter(today)) {
			setDateError('Contract expiry date must be in the future');
		} else {
			setDateError('');
		}

		setComplianceData((prev) => ({
			...prev,
			contractExpiryDate: parsedDate,
		}));
	};

	const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
	const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.PDF,.DOC,.DOCX,.JPG,.JPEG,.PNG';

	const handleFileUpload = (fileOrFiles, fileType = 'justification') => {
		if (fileType === 'labor-contract') {
			// Labor contract is always a single file
			const file = fileOrFiles?.file || fileOrFiles;
			if (file && file.size > MAX_FILE_SIZE) {
				setLaborContractUploadStatus('error');
				setLaborContractFileError('File exceeds the maximum size of 5MB.');
				setRejectedLaborContractFileName(file.name);
				return;
			}
			if (file && !isFileTypeAccepted(file, ACCEPTED_FILE_TYPES)) {
				setLaborContractUploadStatus('error');
				setLaborContractFileError('File format not accepted. Please use an accepted format.');
				setRejectedLaborContractFileName(file.name);
				return;
			}
			setLaborContractFileError('');
			setRejectedLaborContractFileName(null);
			setLaborContractUploadStatus('uploading');
			setLaborContractFile(fileOrFiles);
			setLaborContractUploadStatus('done');
		} else {
			// Justification files come as an array from MultiUploadDragger
			const filesToAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
			const processedFiles = [];

			filesToAdd.forEach((fileObj) => {
				const file = fileObj?.file || fileObj;
				if (file && file.size > MAX_FILE_SIZE) {
					processedFiles.push({
						...fileObj,
						error: true,
						errorMessage: 'File exceeds the maximum size of 5MB'
					});
				} else if (file && !isFileTypeAccepted(file, ACCEPTED_FILE_TYPES)) {
					processedFiles.push({
						...fileObj,
						error: true,
						errorMessage: 'File format not accepted. Please use an accepted format.'
					});
				} else {
					processedFiles.push(fileObj);
				}
			});

			if (processedFiles.length > 0) {
				setJustificationFiles((prev) => {
					const updated = [...prev, ...processedFiles];
					// Check if there are any files with errors (size or format) in the updated list
					const hasErrors = updated.some(file => file.error === true);
					if (hasErrors) {
						setJustificationFilesError('One or more files have errors. Remove or replace them to submit.');
					} else {
						setJustificationFilesError('');
					}
					return updated;
				});
			}
		}
	};

	const handleFileRemove = (id, fileType = 'justification') => {
		if (fileType === 'labor-contract') {
			setLaborContractFile(null);
			setLaborContractFileError('');
			setRejectedLaborContractFileName(null);
			setLaborContractUploadStatus('new');
			return;
		} else {
			setJustificationFiles((prev) => {
				const updated = prev.filter(f => f.id !== id);
				// Check if there are any files with errors in the remaining files
				const hasErrors = updated.some(file => file.error === true);
				if (hasErrors) {
					setJustificationFilesError('One or more files have errors. Remove or replace them to submit.');
				} else {
					setJustificationFilesError('');
				}
				return updated;
			});
		}
	};

	const handleSubmit = async () => {
		const submissionProps = {
			'Event Type': 'Log',
			'Action Taken': isRFI ? 'CLL RFI Response Submission' : 'CLL Resolution Creation',
			'User Identification': currentUser?.user?.userName,
			'Event Source': 'Payroll Portal Application',
			'Details': 'Card Load Limit Submission',
			'Compliance Case ID': complianceCaseId,
			'Employee ID': employeeId,
			'Resolution Type': resolutionType,
		}
		try {
			setIsSubmitLoading(true);

			// Create FormData for multipart/form-data submission
			const formData = new FormData();
			formData.append('ResolutionType', resolutionType);

			// Add required fields to FormData
			if (complianceCaseId) {
				formData.append('ComplianceCaseId', complianceCaseId);
			}
			if (employeeId) {
				formData.append('C3EmpRegId', employeeId);
			}
			if (laborContractFile && !laborContractFileError && (laborContractFile instanceof File || laborContractFile.file)) {
				formData.append('LaborContract', laborContractFile.file || laborContractFile);
			}

			if (complianceData.contractType) {
				formData.append('ContractType', complianceData.contractType);
			}
			if (complianceData.contractExpiryDate) {
				formData.append('ExpiryDate', complianceData.contractExpiryDate);
			}
			if (complianceData.justification) {
				formData.append('Comment', complianceData.justification);
			}

			// Only submit files without errors
			justificationFiles
				.filter(fileObj => !fileObj.error)
				.forEach((fileObj) => {
					const file = fileObj.file || fileObj;
					formData.append('JustificationFiles', file);
				});

			selectedJustificationOptions.forEach((option) => {
				formData.append('JustificationOptions', option.id);
			});

			if (isRFI) {
				await complianceService.rfiUpdate(formData);
			} else {
				await complianceService.createViolationResolution(formData);
			}

			await employeeService.updateViolationActionMessage({
				ComplianceCaseId: complianceCaseId,
				Action: 'No action needed, request under review.'
			}, isDormantEmployee);

			setIsSubmittedSuccessfully(true);
			trackCLLSubmission(submissionProps);

		} catch (err) {
			console.error('Error submitting compliance data:', err);
			trackCLLSubmission({ ...submissionProps, 'Error': err.message });
		} finally {
			setIsSubmitLoading(false);
			appInsights.core.flush();
		}
	};

	const handleGoBack = () => {
		history.push('/employees/manage_employee');
	};

	const handleJustificationOptionsChange = (selectedOptions) => {
		setSelectedJustificationOptions(selectedOptions);
	};

	const handleDownloadDocument = async (documentId, fileName) => {
		try {
			const response = await complianceService.downloadDocument(documentId);
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			message.error('Failed to download document');
		}
	};

	if (loading) {
		return <ContentLoader />;
	}

	if (isSubmittedSuccessfully) {
		return <ComplianceSuccess onGoBack={handleGoBack} />;
	}

	return (
		<ComplianceViolationsPresentational
			complianceData={complianceData}
			showCommentSection={showCommentSection}
			uploadStatus={laborContractUploadStatus}
			justificationOptions={justificationOptions}
			isSubmitLoading={isSubmitLoading}
			laborContractFile={laborContractFile}
			laborContractFileError={laborContractFileError}
			rejectedLaborContractFileName={rejectedLaborContractFileName}
			justificationFiles={justificationFiles}
			justificationFilesError={justificationFilesError}
			previousJustificationDocuments={previousJustificationDocuments}
			requiresJustificationDocument={requiresJustificationDocument}
			requiredDocumentsList={requiredDocumentsList}
			onContractTypeChange={handleContractTypeChange}
			onDateChange={handleDateChange}
			onJustificationChange={handleJustificationChange}
			onJustificationOptionsChange={handleJustificationOptionsChange}
			onFileUpload={handleFileUpload}
			onFileRemove={handleFileRemove}
			onDownloadDocument={handleDownloadDocument}
			onSubmit={handleSubmit}
			onGoBack={handleGoBack}
			isFormValid={isFormValid}
			dateError={dateError}
			isRFI={isRFI}
			loadError={loadError}
			onRetry={() => loadAllData()}
			onBackToEmployees={() => history.push('/employees/manage_employee')}
		/>
	);
};

export default ComplianceViolationsFunctional;
